import React from 'react'
import {Box} from '@mui/material'
import moment from 'moment'
import DataTable, {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem} from 'src/components-new/data/Filters'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {STATUS} from 'src/constants/constants'

const activitesStates = [
  {value: 'TO_BE_PLANNED', label: 'Da pianificare'},
  {value: 'TO_BE_CONFIRMED', label: 'Da confermare'},
  {value: 'TO_BE_CONCLUDED', label: 'Da concludere'},
  {value: 'TO_BE_VERIFIED', label: 'Da verificare'},
  {value: 'COMPLETED', label: 'Completata'},
  {value: 'CANCELLED', label: 'Cancellata'},
]

type ActivityRecord = Record<string, any>

interface ProfileActivitiesTabProps {
  pagination: PaginationState
  onPaginationChange: (next: any) => void
  activities: ActivityRecord[]
  activitiesCount: number
  onFiltersSubmit: (values: any) => void
  onActivityClick: (activity: ActivityRecord) => void
  loadStatus: string | undefined
  columns?: any[]
}

const DEFAULT_COLUMNS: ColumnDef<ActivityRecord>[] = [
  {
    key: '_id',
    header: 'CODICE ATTIVITÀ',
    render: (row) => row._id,
  },
  {
    key: 'title',
    header: 'NOME',
    render: (row) => row.title,
  },
  {
    key: 'plannedDate',
    header: 'DATA/ORA',
    render: (row) => row.plannedDate ? moment(row.plannedDate).format('DD MMMM YYYY HH:mm') : '—',
  },
  {
    key: 'activityTypeReadable',
    header: 'TIPO',
    render: (row) => row.activityTypeReadable ?? '—',
  },
  {
    key: 'stateReadable',
    header: 'STATO',
    render: (row) => row.stateReadable ?? '—',
  },
]

function buildColumns(columnsConfig: any[] | undefined): ColumnDef<ActivityRecord>[] {
  if (!columnsConfig || columnsConfig.length === 0) return DEFAULT_COLUMNS

  return columnsConfig.map((col: any) => ({
    key: col.id,
    header: col.label,
    render: (row: ActivityRecord) => {
      if (col.type === 'date' && row[col.id]) {
        return moment(row[col.id]).format(col.format ?? 'DD MMMM YYYY HH:mm')
      }
      return row[col.id] ?? '—'
    },
  }))
}

export default function ProfileActivitiesTab({
  pagination,
  onPaginationChange,
  activities,
  activitiesCount,
  onFiltersSubmit,
  onActivityClick,
  loadStatus,
  columns,
}: ProfileActivitiesTabProps): JSX.Element {
  const filterConfig: FilterItem[] = [
    {id: 'code', label: 'Codice attività', type: 'text'},
    {id: 'name', label: 'Nome', type: 'text'},
    {id: 'rangeDates', label: 'Data', type: 'rangePicker'},
    {id: 'state', label: 'Stato', type: 'select', options: activitesStates},
  ]

  const tableCols = buildColumns(columns)

  return (
    <Box>
      {loadStatus === STATUS.REJECTED && (
        <InlineMessage severity="error" sx={{mb: 2}}>
          {'Errore nel caricare le attività'}
        </InlineMessage>
      )}
      <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 2}}>
        <Filters config={filterConfig} onSubmit={onFiltersSubmit} />
      </Box>
      <DataTable<ActivityRecord>
        columns={tableCols}
        rows={activities}
        keyField="_id"
        pagination={pagination}
        totalElements={activitiesCount}
        onPaginationChange={onPaginationChange}
        onRowClick={onActivityClick}
        loading={loadStatus === STATUS.PENDING}
      />
    </Box>
  )
}
